import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { addToCart } from '../../store/slices/cartSlice';
import {
  Box, Typography, Paper, Grid, Chip, Divider, CircularProgress, Button,
  TextField, IconButton, Avatar, Alert, ImageList, ImageListItem, ImageListItemBar
} from '@mui/material';
import {
  ArrowBack, Build, Inventory2, CloudUpload, Delete, Lock, Save, ShoppingCart
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const ASSET_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const statusMeta = {
  pending_review: { label: 'Pending Review', color: 'warning' },
  priced: { label: 'Priced — ready to order', color: 'info' },
  added_to_order: { label: 'Added to Order', color: 'primary' },
  locked: { label: 'Locked (order confirmed)', color: 'success' },
  declined: { label: 'Declined', color: 'error' }
};

const CustomizationRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get(`/customization-requests/${id}`);
      setRequest(data.data);
      setDescription(data.data.description || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this request.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const saveDescription = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/customization-requests/${id}`, { description });
      setRequest(data.data);
      toast.success('Description updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
    setSaving(false);
  };

  const handlePickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/customization-requests/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setRequest(data.data);
      toast.success('Attachment added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload attachment');
    }
    setUploading(false);
  };

  const removeAttachment = async (attachmentId) => {
    try {
      const { data } = await api.delete(`/customization-requests/${id}/attachments/${attachmentId}`);
      setRequest(data.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove attachment');
    }
  };

  const addToCartWithCustomization = () => {
    const product = request.product;
    dispatch(addToCart({
      productId: product._id,
      name: product.name,
      osesgCode: product.osesgCode,
      image: product.images?.find((i) => i.isPrimary)?.url || product.images?.[0]?.url,
      unitPrice: product.pricing?.basePrice || 0,
      quantity: 1,
      supplierId: request.supplier?._id || request.supplier,
      customization: {
        requestId: request._id,
        description: request.description,
        price: request.customizationPrice
      }
    }));
    toast.success('Added to cart with customization! Redirecting to checkout...');
    navigate('/customer/cart');
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error || !request) return <Alert severity="error">{error || 'Request not found'}</Alert>;

  const meta = statusMeta[request.status] || { label: request.status, color: 'default' };
  const image = request.product?.images?.find((i) => i.isPrimary) || request.product?.images?.[0];
  const canEdit = !request.locked;
  const canAddToCart = !request.locked && request.status === 'priced' && typeof request.customizationPrice === 'number';

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer/customization-requests')} sx={{ mb: 2 }}>
        Back to Customization Requests
      </Button>

      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h5" fontWeight={700}><Build sx={{ verticalAlign: 'middle', mr: 1 }} />Customization Request</Typography>
        <Chip label={meta.label} color={meta.color} />
      </Box>

      {request.locked && (
        <Alert severity="info" icon={<Lock />} sx={{ mb: 2 }}>
          This request is locked because its order has been confirmed — the description and attachments can no
          longer be changed, but remain visible for reference.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Product</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {image?.url
                ? <Avatar src={image.url} variant="rounded" sx={{ width: 64, height: 64 }} />
                : <Avatar variant="rounded" sx={{ width: 64, height: 64 }}><Inventory2 /></Avatar>}
              <Box>
                <Typography fontWeight={600} component={Link} to={`/products/${request.product?._id}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
                  {request.product?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">{request.product?.osesgCode}</Typography>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Requested Customization</Typography>
            <TextField
              fullWidth multiline minRows={3} value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
              sx={{ mb: canEdit ? 1.5 : 0 }}
            />
            {canEdit && (
              <Button variant="outlined" size="small" startIcon={<Save />} disabled={saving || description === request.description}
                onClick={saveDescription}>
                {saving ? 'Saving…' : 'Save Description'}
              </Button>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="h6">Photos & Videos</Typography>
              {canEdit && (
                <>
                  <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={handlePickFile} />
                  <Button size="small" variant="outlined" startIcon={<CloudUpload />} disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}>
                    {uploading ? 'Uploading…' : 'Add Photo/Video'}
                  </Button>
                </>
              )}
            </Box>
            {request.attachments?.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No attachments yet.</Typography>
            ) : (
              <ImageList cols={3} gap={8}>
                {request.attachments.map((a) => (
                  <ImageListItem key={a._id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    {a.type === 'video' ? (
                      <video src={`${ASSET_BASE_URL}${a.url}`} controls style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                    ) : (
                      <img src={`${ASSET_BASE_URL}${a.url}`} alt={a.name} style={{ width: '100%', height: 140, objectFit: 'cover' }} />
                    )}
                    {canEdit && (
                      <ImageListItemBar
                        sx={{ background: 'rgba(0,0,0,0.4)' }}
                        position="top"
                        actionIcon={
                          <IconButton size="small" sx={{ color: 'white' }} onClick={() => removeAttachment(a._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        }
                        actionPosition="right"
                      />
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Pricing</Typography>
            {typeof request.customizationPrice === 'number' ? (
              <Typography variant="h5" fontWeight={800} color="secondary.main" gutterBottom>
                ${request.customizationPrice.toLocaleString()}
              </Typography>
            ) : (
              <Alert severity="warning" sx={{ mb: 1.5 }}>Awaiting a price from our team.</Alert>
            )}
            <Divider sx={{ my: 1.5 }} />
            {canAddToCart && (
              <Button fullWidth variant="contained" startIcon={<ShoppingCart />} onClick={addToCartWithCustomization}>
                Add to Cart with Customization
              </Button>
            )}
            {request.status === 'added_to_order' && (
              <Typography variant="body2" color="text.secondary">
                Already added to an order.
              </Typography>
            )}
            {request.status === 'declined' && (
              <Alert severity="error">This customization request was declined.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomizationRequestDetail;
